import { useState, useEffect, useCallback, useRef } from 'react';
import API from '@/lib/api';

export function useOnboarding({ onExampleSeeded } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const seededOnce = useRef(false);

  const fetchState = useCallback(async () => {
    try {
      const res = await API.get('/onboarding/intro-state');
      setData(res.data);
      return res.data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const res = await fetchState();
      if (!active || !res) return;
      const st = res.state || {};
      // Auto-seed a labelled example for brand-new users (once).
      if (!st.example_seeded && !st.example_dismissed && !seededOnce.current) {
        seededOnce.current = true;
        try {
          const r = await API.post('/onboarding/seed-example');
          if (r.data?.seeded && onExampleSeeded) onExampleSeeded();
          await fetchState();
        } catch { /* non-blocking */ }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchState]);

  // Auto-refresh whenever the browser tab regains focus — catches cases like
  // "user verified their email in another tab" or "user completed profile,
  // switched tabs, came back" without needing every mutation site to know
  // about the checklist.
  useEffect(() => {
    const onFocus = () => { fetchState(); };
    const onVisible = () => { if (document.visibilityState === 'visible') fetchState(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchState]);

  const patch = useCallback(async (updates) => {
    setData(d => (d ? { ...d, state: { ...d.state, ...updates } } : d));
    try {
      await API.patch('/onboarding/intro-state', updates);
    } catch { /* optimistic */ }
  }, []);

  const removeExample = useCallback(async () => {
    try {
      await API.delete('/onboarding/example');
      await fetchState();
      return true;
    } catch {
      return false;
    }
  }, [fetchState]);

  return { data, loading, refetch: fetchState, patch, removeExample };
}
